import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Package, Loader2, AlertCircle, Phone, KeyRound, 
  CheckCircle2, ArrowRight, ArrowLeft, RotateCw, MessageSquare, Mail, Building2, ShieldCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CustomerLogin() {
  const navigate = useNavigate();

  // Step 1: Mobile Entry | Step 2: OTP Verification
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSentInfo, setOtpSentInfo] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // Timer countdown for resending OTP
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => setCountdown(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  // Request OTP via WhatsApp and Email
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');

    const cleanNumber = mobile.replace(/\D/g, '');
    if (cleanNumber.length < 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client-auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanNumber }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }

      setOtpSentInfo(data);
      setStep(2);
      setCountdown(45); // 45 seconds before resend

      // If dev OTP is available in local test mode, prefill for convenience
      if (data.dev_otp) {
        setOtp(data.dev_otp);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and redirect to Client Dashboard
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp.trim() || otp.trim().length !== 6) {
      setError('Please enter the 6-digit verification OTP');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client-auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: mobile.replace(/\D/g, ''),
          otp: otp.trim()
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'OTP verification failed');
      }

      // Store client session tokens
      localStorage.setItem('customer_token', data.access_token);
      localStorage.setItem('customer_info', JSON.stringify(data.customer));

      // Redirect to customer portal dashboard
      navigate('/customer-portal/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const homeUrl = typeof window !== 'undefined' && window.location.port === '3000'
    ? 'http://localhost:3001/'
    : '/';

  return (
    <div className="min-h-screen relative flex flex-col justify-between items-center p-4 sm:p-6 bg-slate-900 overflow-hidden font-ibm">
      {/* Background Logistics Image with Dark Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center z-0 scale-105 filter brightness-75"
        style={{
          backgroundImage: `url('/hero-banner.png')`,
        }}
      />
      {/* High-end Navy Gradient Overlay for optimal contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1a54]/90 via-[#0a1a54]/85 to-[#050d2c]/95 z-0" />

      {/* Top Header Bar with Back to Home button */}
      <div className="w-full max-w-5xl z-10 flex items-center justify-between py-2">
        <a
          href={homeUrl}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold backdrop-blur-md border border-white/20 transition-all shadow-sm group cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </a>

        <div className="hidden sm:flex items-center gap-2 text-white/70 text-xs font-semibold">
          <ShieldCheck className="w-4 h-4 text-[#f1b00b]" />
          <span>Verified Client Portal</span>
        </div>
      </div>

      {/* Center Card */}
      <div className="w-full max-w-md z-10 my-auto py-4">
        <Card className="bg-white/95 backdrop-blur-xl rounded-3xl p-7 sm:p-9 shadow-2xl border border-white/40">
          {/* Header with Stavan Logo */}
          <div className="flex flex-col items-center text-center mb-6">
            <a href={homeUrl} className="block mb-3 hover:opacity-95 transition-opacity">
              <img
                src="/stavan-logo.png"
                alt="Stavan International"
                className="h-12 sm:h-14 w-auto object-contain mx-auto"
              />
            </a>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-[#122b84] text-[11px] font-bold uppercase tracking-wider mb-2 border border-blue-100">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Client &amp; Shipper Portal
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">Client Portal Login</h1>
            <p className="text-xs text-slate-500 font-ibm mt-1">
              Access your shipments, tracking, and booking requests
            </p>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3.5 flex items-start gap-2.5 text-xs font-ibm animate-shake">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1 leading-relaxed">{error}</div>
            </div>
          )}

          {step === 1 ? (
            /* STEP 1: MOBILE NUMBER INPUT */
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <Label htmlFor="mobile" className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-1.5 font-ibm">
                  Registered Mobile Number
                </Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 text-sm font-semibold font-ibm border-r border-slate-200 pr-2.5 my-2">
                    +91
                  </div>
                  <Input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                    placeholder="98765 43210"
                    autoFocus
                    required
                    className="pl-16 h-12 text-base font-semibold font-ibm rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-2 font-ibm flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                  <span>OTP will be sent to your WhatsApp & registered email</span>
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || mobile.length < 10}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl font-ibm transition-all shadow-md shadow-blue-600/20 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Verifying Mobile...</span>
                  </>
                ) : (
                  <>
                    <span>Send Verification OTP</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>

              <div className="pt-2 text-center">
                <span className="text-xs text-slate-400 font-ibm">
                  Need an account or updated number?{' '}
                  <span className="text-blue-600 font-semibold">Contact your Operations Manager</span>
                </span>
              </div>
            </form>
          ) : (
            /* STEP 2: OTP VERIFICATION */
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              {/* Sent Status Card */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 text-xs font-ibm space-y-1.5">
                <div className="flex items-center justify-between text-slate-800">
                  <span className="font-semibold text-slate-900">{otpSentInfo?.customer_name || 'Customer'}</span>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setError(''); }}
                    className="text-blue-600 hover:underline text-[11px] font-bold"
                  >
                    Change Number
                  </button>
                </div>
                <div className="flex items-center gap-3 text-slate-500 text-[11px] pt-0.5">
                  <span className="flex items-center gap-1 text-emerald-700 font-medium">
                    <MessageSquare className="w-3 h-3 text-emerald-600" />
                    WhatsApp: {otpSentInfo?.masked_mobile || mobile}
                  </span>
                  {otpSentInfo?.masked_email && (
                    <span className="flex items-center gap-1 text-blue-700 font-medium">
                      <Mail className="w-3 h-3 text-blue-600" />
                      {otpSentInfo.masked_email}
                    </span>
                  )}
                </div>
              </div>

              {/* Dev Helper Pill (if dev_otp present) */}
              {otpSentInfo?.dev_otp && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2 text-xs font-ibm flex items-center justify-between">
                  <span>Dev OTP: <strong>{otpSentInfo.dev_otp}</strong></span>
                  <button
                    type="button"
                    onClick={() => setOtp(otpSentInfo.dev_otp)}
                    className="text-[11px] font-bold text-amber-900 bg-amber-200/70 px-2 py-0.5 rounded hover:bg-amber-200"
                  >
                    Autofill
                  </button>
                </div>
              )}

              <div>
                <Label htmlFor="otp" className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-1.5 font-ibm">
                  Enter 6-Digit OTP
                </Label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-400 absolute left-3.5 top-4" />
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••••"
                    autoFocus
                    required
                    className="pl-10 h-12 text-center text-xl font-bold tracking-widest font-mono rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl font-ibm transition-all shadow-md shadow-emerald-600/20 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Verifying Code...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    <span>Verify & Enter Portal</span>
                  </>
                )}
              </Button>

              <div className="flex items-center justify-between pt-1 text-xs font-ibm text-slate-500">
                {countdown > 0 ? (
                  <span>Resend code in <strong className="text-slate-700">{countdown}s</strong></span>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="text-blue-600 hover:underline font-bold flex items-center gap-1"
                  >
                    <RotateCw className="w-3 h-3" />
                    <span>Resend OTP on WhatsApp & Email</span>
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Footer note */}
          <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-center gap-1.5 text-[11px] text-slate-400 font-ibm">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>End-to-End Encrypted Customer Access</span>
          </div>
        </Card>
      </div>

      {/* Bottom Footer */}
      <div className="w-full max-w-md z-10 text-center py-2 text-white/60 text-xs">
        &copy; {new Date().getFullYear()} Stavan International. All rights reserved.
      </div>
    </div>
  );
}
